import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { AppCoachDiagramBlock, DiagramBlock } from './AppCoachDiagramBlock.component'
import { AppCoachWebPageBlock, WebPageBlock } from './AppCoachWebPageBlock.component'
import {
  AppCoachCanvaDesignBlock,
  AppCoachConnectCanvaBlock,
  CanvaDesignBlock,
  ConnectCanvaBlock,
} from './AppCoachCanvaBlocks.component'
import {
  AppCoachConnectGoogleDriveBlock,
  AppCoachGoogleDriveFileBlock,
  AppCoachGoogleDrivePickerBlock,
  ConnectGoogleDriveBlock,
  GoogleDriveFileBlock,
  GoogleDrivePickerBlock,
} from './AppCoachGoogleDriveBlocks.component'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TableBlock = {
  type: 'table'
  title?: string
  columns: string[]
  rows: (string | number)[][]
}

export type BarChartBlock = {
  type: 'bar_chart'
  title?: string
  x_key: string
  y_keys: { key: string; label: string; color?: string }[]
  data: Record<string, string | number>[]
}

export type LineChartBlock = {
  type: 'line_chart'
  title?: string
  x_key: string
  y_keys: { key: string; label: string; color?: string }[]
  data: Record<string, string | number>[]
}

export type PieChartBlock = {
  type: 'pie_chart'
  title?: string
  data: { label: string; value: number; color?: string }[]
}

export type MetricBlock = {
  type: 'metric'
  title: string
  value: string | number
  unit?: string
  trend?: 'up' | 'down' | 'flat'
  change?: string
}

export type {
  DiagramBlock,
  WebPageBlock,
  CanvaDesignBlock,
  ConnectCanvaBlock,
  ConnectGoogleDriveBlock,
  GoogleDrivePickerBlock,
  GoogleDriveFileBlock,
}

export type ReportBlock =
  | TableBlock
  | BarChartBlock
  | LineChartBlock
  | PieChartBlock
  | MetricBlock
  | DiagramBlock
  | WebPageBlock
  | CanvaDesignBlock
  | ConnectCanvaBlock
  | ConnectGoogleDriveBlock
  | GoogleDrivePickerBlock
  | GoogleDriveFileBlock

// ---------------------------------------------------------------------------
// Default color palette
// ---------------------------------------------------------------------------

const DEFAULT_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316']

const getColor = (color: string | undefined, index: number) =>
  color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]

// ---------------------------------------------------------------------------
// Block title
// ---------------------------------------------------------------------------

const BlockTitle: React.FC<{ title?: string }> = ({ title }) =>
  title ? <h4 className="text-sm font-semibold text-gray-700 mb-2">{title}</h4> : null

// ---------------------------------------------------------------------------
// Table block
// ---------------------------------------------------------------------------

const TableBlockRenderer: React.FC<{ block: TableBlock }> = ({ block }) => {
  const { t } = useTranslation()
  return (
    <div className="overflow-x-auto">
      <BlockTitle title={block.title} />
      <table className="min-w-full text-sm border-collapse">
        <thead>
          <tr className="bg-indigo-50">
            {block.columns.map((col, i) => (
              <th
                key={i}
                className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide border-b border-gray-200"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row, ri) => (
            <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              {row.map((cell, ci) => (
                <td key={ci} className="px-3 py-2 text-gray-700 border-b border-gray-100">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {block.rows.length === 0 && (
        <p className="text-xs text-gray-400 mt-1">{t('askCL.report.noData')}</p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Bar chart block
// ---------------------------------------------------------------------------

const BarChartBlockRenderer: React.FC<{ block: BarChartBlock }> = ({ block }) => (
  <div>
    <BlockTitle title={block.title} />
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={block.data} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis dataKey={block.x_key} tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip />
        {block.y_keys.length > 1 && <Legend />}
        {block.y_keys.map((yk, i) => (
          <Bar
            key={yk.key}
            dataKey={yk.key}
            name={yk.label}
            fill={getColor(yk.color, i)}
            radius={[3, 3, 0, 0]}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  </div>
)

// ---------------------------------------------------------------------------
// Line chart block
// ---------------------------------------------------------------------------

const LineChartBlockRenderer: React.FC<{ block: LineChartBlock }> = ({ block }) => (
  <div>
    <BlockTitle title={block.title} />
    <ResponsiveContainer width="100%" height={220}>
      <LineChart data={block.data} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis dataKey={block.x_key} tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip />
        {block.y_keys.length > 1 && <Legend />}
        {block.y_keys.map((yk, i) => (
          <Line
            key={yk.key}
            type="monotone"
            dataKey={yk.key}
            name={yk.label}
            stroke={getColor(yk.color, i)}
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  </div>
)

// ---------------------------------------------------------------------------
// Pie chart block
// ---------------------------------------------------------------------------

const PieChartBlockRenderer: React.FC<{ block: PieChartBlock }> = ({ block }) => (
  <div>
    <BlockTitle title={block.title} />
    <ResponsiveContainer width="100%" height={260}>
      <PieChart>
        <Pie
          data={block.data.map((d) => ({ ...d, name: d.label }))}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="45%"
          outerRadius={90}
        >
          {block.data.map((entry, i) => (
            <Cell key={i} fill={getColor(entry.color, i)} />
          ))}
        </Pie>
        <Tooltip formatter={(value: number) => [value, '']} />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  </div>
)

// ---------------------------------------------------------------------------
// Metric block
// ---------------------------------------------------------------------------

const trendIcon = (trend?: 'up' | 'down' | 'flat') => {
  if (trend === 'up') return <span className="text-green-500 text-lg">↑</span>
  if (trend === 'down') return <span className="text-red-500 text-lg">↓</span>
  if (trend === 'flat') return <span className="text-gray-400 text-lg">→</span>
  return null
}

const MetricBlockRenderer: React.FC<{ block: MetricBlock }> = ({ block }) => (
  <div className="bg-indigo-50 rounded-lg px-4 py-3 flex flex-col gap-0.5 min-w-[120px]">
    <span className="text-xs text-gray-500 font-medium">{block.title}</span>
    <div className="flex items-baseline gap-1">
      <span className="text-2xl font-bold text-gray-800">
        {typeof block.value === 'number' ? block.value.toLocaleString() : block.value}
      </span>
      {block.unit && <span className="text-sm text-gray-500">{block.unit}</span>}
    </div>
    {(block.trend || block.change) && (
      <div className="flex items-center gap-1 text-xs text-gray-500">
        {trendIcon(block.trend)}
        {block.change && <span>{block.change}</span>}
      </div>
    )}
  </div>
)

// ---------------------------------------------------------------------------
// Single block dispatcher
// ---------------------------------------------------------------------------

export const AppCoachReportBlock: React.FC<{ block: ReportBlock }> = ({ block }) => {
  const wrapper = 'mt-3 bg-white border border-gray-100 rounded-xl p-4 shadow-sm'

  switch (block.type) {
    case 'table':
      return (
        <div className={wrapper}>
          <TableBlockRenderer block={block} />
        </div>
      )
    case 'bar_chart':
      return (
        <div className={wrapper}>
          <BarChartBlockRenderer block={block} />
        </div>
      )
    case 'line_chart':
      return (
        <div className={wrapper}>
          <LineChartBlockRenderer block={block} />
        </div>
      )
    case 'pie_chart':
      return (
        <div className={wrapper}>
          <PieChartBlockRenderer block={block} />
        </div>
      )
    case 'metric':
      return (
        <div className="mt-3 inline-block">
          <MetricBlockRenderer block={block} />
        </div>
      )
    case 'diagram':
      return (
        <div className={wrapper}>
          <AppCoachDiagramBlock block={block} />
        </div>
      )
    case 'web_page':
      return (
        <div className={wrapper}>
          <AppCoachWebPageBlock block={block} />
        </div>
      )
    case 'canva_design':
      return (
        <div className={wrapper}>
          <AppCoachCanvaDesignBlock block={block} />
        </div>
      )
    case 'connect_canva':
      return (
        <div className={wrapper}>
          <AppCoachConnectCanvaBlock block={block} />
        </div>
      )
    case 'connect_google_drive':
      return (
        <div className={wrapper}>
          <AppCoachConnectGoogleDriveBlock block={block} />
        </div>
      )
    case 'google_drive_picker':
      return (
        <div className={wrapper}>
          <AppCoachGoogleDrivePickerBlock block={block} />
        </div>
      )
    case 'google_drive_file':
      return (
        <div className={wrapper}>
          <AppCoachGoogleDriveFileBlock block={block} />
        </div>
      )
    default:
      return null
  }
}

// ---------------------------------------------------------------------------
// Error boundary — a block that cannot be drawn is left out
// ---------------------------------------------------------------------------

/**
 * Saved blocks come from older messages and servers and are not trusted to have the expected
 * shape (e.g. table rows as objects): one that throws while drawing is skipped instead of taking
 * the whole chat down with it.
 */
class BlockBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error: Error) {
    console.warn('Coach report block could not be drawn', error?.message)
  }

  render() {
    return this.state.failed ? null : this.props.children
  }
}

// ---------------------------------------------------------------------------
// List renderer — groups metrics on one row, stacks other blocks
// ---------------------------------------------------------------------------

export const AppCoachReportBlocks: React.FC<{ blocks: ReportBlock[] }> = ({ blocks }) => {
  if (!blocks || blocks.length === 0) return null

  const metrics = blocks.filter((b) => b.type === 'metric') as MetricBlock[]
  const others = blocks.filter((b) => b.type !== 'metric')

  return (
    <div className="mt-1">
      {metrics.length > 0 && (
        <div className="flex flex-wrap gap-3 mt-3">
          {metrics.map((b, i) => (
            <BlockBoundary key={i}>
              <MetricBlockRenderer block={b} />
            </BlockBoundary>
          ))}
        </div>
      )}
      {others.map((b, i) => (
        <BlockBoundary key={i}>
          <AppCoachReportBlock block={b} />
        </BlockBoundary>
      ))}
    </div>
  )
}

export default AppCoachReportBlocks
